<template>
    <div>
        <dashboard
            v-if="uppy"
            :uppy="uppy"
            :props="{
                showLinkToFileUploadResult: false,
                showProgressDetails: true,
                hideCancelButton: false,
                proudlyDisplayPoweredByUppy: false,
                note: 'Folders with many files might take some time to appear. To upload a folder, drag it here.',
                metaFields: [{ id: 'name', name: 'Name', placeholder: 'Name' }],
                inline: true,
                width: '100%',
                doneButtonHandler: false
            }" />
        <v-alert v-if="error" text type="error" class="mt-4">{{ error }}</v-alert>
        <v-alert v-if="nameCollisions.length" type="info" class="mt-4" text>
            The following files were renamed because of name collisions:
            <ul>
                <li v-for="collision in nameCollisions" :key="collision.originalName">{{ collision.originalName }} -> {{ collision.finalName }}</li>
            </ul>
        </v-alert>
    </div>
</template>

<script>
import { Dashboard } from '@uppy/vue'
import { mapState } from 'vuex'

import Uppy from '@uppy/core'
import Tus from '@uppy/tus'
import { finalizeTusUploads, isTusFileResult, tusEndpoint, tusOptions, uploadErrorResponse } from '@/utils/tusUpload'
import axios from 'axios'
import { DateTime } from 'luxon'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

export default {
    name: 'UppyUpload',
    components: {
        Dashboard
    },
    props: {
        endpoint: {
            type: String,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        reset: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uppy: null,
            error: '',
            httpStatusCodes: [],
            nameCollisions: []
        }
    },
    created() {
        this.tusFinalizers = new Set()
        this.initUppy()
    },
    beforeDestroy() {
        this.closeUppy()
    },
    methods: {
        closeUppy() {
            this.tusFinalizers.forEach(cancel => cancel())
            this.uppy?.close({ reason: 'unmount' })
        },
        initUppy() {
            this.closeUppy()
            this.error = ''
            this.httpStatusCodes = []
            this.nameCollisions = []
            const uploadEndpoint = tusEndpoint(this.$axios.defaults.baseURL, this.endpoint)
            const uppy = new Uppy().use(
                Tus,
                tusOptions(uploadEndpoint, () => this.$auth.getTokenSilently())
            )
            this.uppy = uppy
            const tus = uppy.getPlugin('Tus')
            uppy.removeUploader(tus.handleUpload)
            // Dashboard retries after a copy failure must not upload the bytes again.
            uppy.addUploader(fileIDs =>
                tus.handleUpload(fileIDs.filter(id => !(uppy.getFile(id)?.progress?.uploadComplete && uppy.getFile(id)?.response?.uploadURL)))
            )
            uppy.addPostProcessor(fileIDs =>
                finalizeTusUploads(
                    uppy,
                    fileIDs,
                    url => this.$axios.get(url, { headers: { 'Tus-Resumable': '1.0.0' }, 'axios-retry': { retries: 0 } }),
                    cancel => {
                        this.tusFinalizers.add(cancel)
                        return () => this.tusFinalizers.delete(cancel)
                    }
                )
            )
            this.uppy.on('file-added', file => {
                this.uppy.setFileMeta(file.id, {
                    operation: 'CREATE'
                })
            })
            this.uppy.on('upload-error', (file, error, response) => {
                this.$emit('uploading', 'error')

                response = uploadErrorResponse(error, response)
                if (response.body?.code === 'max_quota_exceeded') {
                    this.error = 'File upload failed because you exceeded your file quota!'
                } else {
                    this.error = 'File upload failed due to network issues.'
                }

                // group errors by http status codes to not spam slack
                if (this.httpStatusCodes.includes(response.status)) return
                this.httpStatusCodes.push(response.status)

                const slackMessage = {
                    blocks: [
                        {
                            type: 'header',
                            text: {
                                type: 'plain_text',
                                text: ':fire: File upload error',
                                emoji: true
                            }
                        },
                        {
                            type: 'section',
                            text: {
                                type: 'mrkdwn',
                                text: '*Request:*\n```' + uploadEndpoint + '```'
                            }
                        },
                        {
                            type: 'section',
                            text: {
                                type: 'mrkdwn',
                                text: '*Error:*\n```' + error.stack + '```'
                            }
                        },
                        {
                            type: 'section',
                            text: {
                                type: 'mrkdwn',
                                text: '*Response:*\n```' + JSON.stringify(response) + '```'
                            }
                        },
                        {
                            type: 'section',
                            fields: [
                                {
                                    type: 'mrkdwn',
                                    text: '*Time:*\n' + DateTime.now().toFormat(this.$appConfig?.VUE_APP_LONGDATETIMEFORMAT)
                                },
                                {
                                    type: 'mrkdwn',
                                    text: '*Authentication:*\nRefreshed before each upload request'
                                }
                            ]
                        },
                        {
                            type: 'section',
                            text: {
                                type: 'mrkdwn',
                                text: '*User:*\n```' + JSON.stringify(this.userInfo) + '```'
                            }
                        },
                        {
                            type: 'section',
                            text: {
                                type: 'mrkdwn',
                                text: '*File:*\n```' + JSON.stringify(file) + '```'
                            }
                        }
                    ]
                }
                this.postToSlack(slackMessage)
            })
            this.uppy.on('complete', result => {
                this.httpStatusCodes = []
                const registered = result.successful.filter(file => isTusFileResult(file.response?.body))
                this.$emit('uploading', result.failed.length || registered.length !== result.successful.length ? 'error' : 'success')

                if (registered.length) {
                    const newFiles = registered.map(file => file.response.body)
                    this.$store.dispatch('snapshotStore/updateLastUploadedFiles', {
                        newData: newFiles,
                        updateMode: 'add'
                    })

                    registered.forEach(file => {
                        const response = file.response.body
                        if (response.collision_info && response.collision_info.was_renamed) {
                            this.nameCollisions.push({
                                originalName: response.collision_info.original_name,
                                finalName: response.collision_info.final_name
                            })
                        }
                    })
                }
            })
            this.uppy.on('upload', data => {
                this.$emit('uploading', 'uploading')
            })
        },
        async postToSlack(message) {
            try {
                const simpleAxios = axios.create()
                await simpleAxios.post('https://hooks.slack.com/services/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', message, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                })
            } catch (error) {
                console.log(error)
            }
        }
    },
    computed: {
        ...mapState(['userInfo'])
    },
    watch: {
        reset(need) {
            if (!need) return
            this.uppy.cancelAll()
            this.error = ''
        },
        endpoint() {
            this.initUppy()
        }
    }
}
</script>
